import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PublishIcon from "@mui/icons-material/Publish";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import LinearProgress from "@mui/material/LinearProgress";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";
import { api } from "../api/client";
import {
  COULEURS_STATUT_CHANTIER,
  type Chantier,
  LIBELLES_STATUT_CHANTIER,
  montantLisible,
} from "./Chantiers";

type Media = {
  id: number;
  type: string;
  alt: string;
  urlMiniature: string;
  largeur: number;
  hauteur: number;
};

const ORDRE_GALERIES: { type: string; titre: string }[] = [
  { type: "avant", titre: "Avant" },
  { type: "pendant", titre: "Pendant" },
  { type: "apres", titre: "Après" },
  { type: "libre", titre: "Autres" },
];

/**
 * Fiche chantier.
 *
 * Le bouton « Publier en réalisation » est le pivot du référencement local :
 * il transforme un chantier réel en page publique, sans ressaisie. C'est ce
 * qui rend tenable la stratégie de pages par commune — chaque chantier fournit
 * le contenu authentique qu'une page générée par gabarit n'aurait pas.
 */
export function ChantierFiche() {
  const { id } = useParams();
  const naviguer = useNavigate();
  const client = useQueryClient();

  const chantier = useQuery({
    queryKey: ["chantiers", "fiche", id],
    queryFn: () => api.get<{ chantier: Chantier }>(`/api/chantiers/${id}`),
    enabled: id !== undefined,
  });

  const medias = useQuery({
    queryKey: ["medias", "chantier", id],
    queryFn: () => api.get<{ medias: Media[] }>(`/api/medias?chantierId=${id}`),
    enabled: id !== undefined,
  });

  const publier = useMutation({
    mutationFn: () => api.post<{ realisationId: number }>(`/api/chantiers/${id}/publier`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["chantiers"] });
      client.invalidateQueries({ queryKey: ["contenu", "realisations"] });
      naviguer("/contenu/realisations");
    },
  });

  if (chantier.isPending) return <LinearProgress />;
  if (chantier.isError) return <Alert severity="error">Chantier introuvable.</Alert>;

  const fiche = chantier.data.chantier;
  const photos = medias.data?.medias ?? [];
  const sansAlt = photos.filter((photo) => photo.alt.trim() === "");
  const erreurPublication = publier.error as (Error & { erreurs?: Record<string, string> }) | null;

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => naviguer("/chantiers")}>
          Chantiers
        </Button>
      </Stack>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{ alignItems: { md: "center" }, justifyContent: "space-between" }}
      >
        <Box>
          <Typography variant="h5" component="h1">
            {fiche.titre}
          </Typography>
          <Typography color="text.secondary">
            {fiche.clientNom}
            {fiche.communeNom ? ` · ${fiche.communeNom}` : ""}
          </Typography>
        </Box>
        <Chip
          label={LIBELLES_STATUT_CHANTIER[fiche.statut]}
          color={COULEURS_STATUT_CHANTIER[fiche.statut]}
        />
      </Stack>

      <Paper sx={{ p: 3 }}>
        <Stack spacing={1}>
          <Ligne intitule="Client" valeur={fiche.clientNom} />
          <Ligne intitule="Adresse" valeur={fiche.adresse || "—"} />
          <Ligne intitule="Commune" valeur={fiche.communeNom || "—"} />
          <Ligne
            intitule="Dates"
            valeur={`${fiche.dateDebut ?? "—"} → ${fiche.dateFin ?? "en cours"}`}
          />
          <Ligne intitule="Budget" valeur={montantLisible(fiche.budget)} />
          {fiche.description && <Ligne intitule="Description" valeur={fiche.description} />}
          {fiche.notesInternes && <Ligne intitule="Notes internes" valeur={fiche.notesInternes} />}
        </Stack>
      </Paper>

      {/* --- Publication en réalisation ------------------------------------ */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Page publique
        </Typography>

        {fiche.realisationId !== null ? (
          <Alert severity="success">
            Ce chantier est déjà publié en réalisation.{" "}
            <Link href="/contenu/realisations">Voir la réalisation</Link>
          </Alert>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Publier ce chantier crée une page de réalisation reprenant ses photos. C'est ce
              contenu réel qui fait vivre la page de sa commune — un texte générique n'aurait pas le
              même effet.
            </Typography>

            {sansAlt.length > 0 && (
              <Alert severity="warning">
                <AlertTitle>
                  {sansAlt.length === 1
                    ? "Une photo n'a pas de description"
                    : `${sansAlt.length} photos n'ont pas de description`}
                </AlertTitle>
                La publication sera refusée tant qu'une image n'est pas décrite. Complétez-les
                depuis la <Link href="/medias">médiathèque</Link>.
              </Alert>
            )}

            {erreurPublication && (
              <Alert severity="error">
                {erreurPublication.erreurs
                  ? Object.values(erreurPublication.erreurs).join(" ")
                  : erreurPublication.message}
              </Alert>
            )}

            <Box>
              <Button
                variant="contained"
                startIcon={<PublishIcon />}
                disabled={publier.isPending || photos.length === 0}
                onClick={() => publier.mutate()}
              >
                {publier.isPending ? "Publication…" : "Publier en réalisation"}
              </Button>
              {photos.length === 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1 }}
                >
                  Ajoutez au moins une photo avant de publier.
                </Typography>
              )}
            </Box>
          </Stack>
        )}
      </Paper>

      {/* --- Galeries ------------------------------------------------------- */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Photos
        </Typography>

        {medias.isPending ? (
          <LinearProgress />
        ) : photos.length === 0 ? (
          <Typography color="text.secondary">
            Aucune photo rattachée. Ajoutez-en depuis la <Link href="/medias">médiathèque</Link>.
          </Typography>
        ) : (
          <Stack spacing={3} divider={<Divider />}>
            {ORDRE_GALERIES.map(({ type, titre }) => {
              const groupe = photos.filter((photo) => photo.type === type);
              if (groupe.length === 0) return null;

              return (
                <Box key={type}>
                  <Typography variant="subtitle2" gutterBottom>
                    {titre} ({groupe.length})
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gap: 1.5,
                      gridTemplateColumns: {
                        xs: "1fr 1fr",
                        sm: "repeat(3, 1fr)",
                        lg: "repeat(4, 1fr)",
                      },
                    }}
                  >
                    {groupe.map((photo) => (
                      <Card key={photo.id}>
                        <CardMedia
                          component="img"
                          src={photo.urlMiniature}
                          alt={photo.alt || `Photo « ${titre} » du chantier`}
                          loading="lazy"
                          sx={{ aspectRatio: "4 / 3", objectFit: "cover", bgcolor: "grey.100" }}
                        />
                      </Card>
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Paper>
    </Stack>
  );
}

function Ligne({ intitule, valeur }: { intitule: string; valeur: string }) {
  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
      <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 160 }}>
        {intitule}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
        {valeur}
      </Typography>
    </Stack>
  );
}
